<template>
    <card :title="pageTitle">
        <div v-if="loading" class="text-center py-4">
            <b-spinner /> Loading batch details...
        </div>

        <div v-else-if="batch">
            <!-- Batch Info Section -->
            <b-card class="mb-4">
                <b-card-title>Batch Information</b-card-title>
                <b-row>
                    <b-col cols="6">
                        <p class="mb-1"><strong>Compound:</strong> {{ batch.compound?.name }}</p>
                        <p class="mb-1"><strong>Batch Number:</strong> {{ batch.batchNumber }}</p>
                        <p class="mb-1"><strong>Batch Size:</strong> {{ batch.batchSize }} {{ batch.compound?.unitOfMeasure }}</p>
                        <p class="mb-1">
                            <strong>Package Size:</strong>
                            {{ batch.packageSize ? `${batch.packageSize} ${batch.compound?.unitOfMeasure}` : 'N/A' }}
                        </p>
                    </b-col>
                    <b-col cols="6">
                        <p class="mb-1"><strong>Lot Number:</strong> {{ batch.lotNumber || 'N/A' }}</p>
                        <p class="mb-1"><strong>Beyond Use Date:</strong> {{ batch.beyondUseDate ? formatDate(batch.beyondUseDate) : 'N/A' }}</p>
                        <p class="mb-1">
                            <strong>Mode:</strong>
                            <b-badge :variant="batch.rxNumber ? 'info' : 'primary'">
                                {{ batch.rxNumber ? 'Prescription' : 'Batch' }}
                            </b-badge>
                        </p>
                        <p class="mb-1" v-if="batch.rxNumber">
                            <strong>Rx Number:</strong> {{ batch.rxNumber }}-{{ batch.rfNumber }}
                        </p>
                        <p class="mb-1"><strong>Created:</strong> {{ formatDateTime(batch.created) }} by {{ batch.createdBy }}</p>
                    </b-col>
                </b-row>
            </b-card>

            <!-- Review Flags: conditions on the record a pharmacist must acknowledge before approving.
                 Derived by the server from the ingredient rows; the acknowledgement is stored on the batch. -->
            <b-card v-if="reviewFlagDescriptions.length > 0" class="mb-4"
                :border-variant="reviewAcknowledged ? 'success' : 'warning'">
                <b-card-title>
                    <i class="fas fa-flag" :class="reviewAcknowledged ? 'text-success' : 'text-warning'" />
                    Review Flags
                    <b-badge :variant="reviewAcknowledged ? 'success' : 'warning'" class="ml-2">
                        {{ reviewAcknowledged ? 'Acknowledged' : `${reviewFlagDescriptions.length} outstanding` }}
                    </b-badge>
                </b-card-title>
                <p class="small text-muted mb-2">
                    These conditions on the batch record must be acknowledged by a pharmacist before the batch can be
                    approved. Acknowledging records that they were reviewed; it does not approve the batch.
                </p>
                <ul class="pl-3 mb-3">
                    <li v-for="(flag, index) in reviewFlagDescriptions" :key="index" class="mb-1">
                        <b-badge variant="warning" class="mr-1">{{ flag.label }}</b-badge>
                        {{ flag.detail }}
                    </li>
                </ul>
                <div v-if="reviewAcknowledged" class="small text-success">
                    <i class="fas fa-check-circle" />
                    Acknowledged by {{ batch.reviewAcknowledgedBy }} on {{ formatDateTime(batch.reviewAcknowledgedDate) }}
                </div>
                <PositiveIdGate
                    v-else
                    :enabled="positiveIdEnabled && positiveIdConfigLoaded && positiveIdTargetId !== null"
                    :enabled-methods="positiveIdMethods"
                    :action-type="PositiveIdActionType.Compound"
                    :target-entity-type="PositiveIdTargetEntityType.CompoundBatch"
                    :target-entity-id="positiveIdTargetId || ''"
                    @verified="onPositiveIdVerifiedForAcknowledge">
                    <template #default="{ requestVerification, verifying }">
                        <b-button
                            variant="warning"
                            @click="requestVerification"
                            :disabled="acknowledging || submitting || verifying || positiveIdNotReady">
                            <b-spinner v-if="acknowledging || verifying" small class="mr-1" />
                            <i v-else class="fas fa-clipboard-check" /> Acknowledge Review Flags
                        </b-button>
                    </template>
                </PositiveIdGate>
            </b-card>

            <!-- Ingredients Section: the batch record's weigh-out, COA and lot snapshot -->
            <b-card v-if="batch.ingredients && batch.ingredients.length > 0" class="mb-4">
                <b-card-title>Ingredients Used</b-card-title>
                <CompoundBatchIngredientRecord
                    :ingredients="batch.ingredients"
                    :decimal-places="activityFactorSettings.decimalPlaces" />
            </b-card>

            <!-- Steps Section -->
            <b-card class="mb-4">
                <b-card-title>
                    Preparation Steps
                    <small class="text-muted ml-2">
                        ({{ verifiedStepCount }} / {{ requiredStepCount }} required verifications completed)
                    </small>
                </b-card-title>

                <div v-if="!batch.steps || batch.steps.length === 0">
                    <p class="text-muted">No preparation steps recorded for this batch.</p>
                </div>

                <div v-else>
                    <div v-for="step in batch.steps" :key="step.id" class="mb-3">
                        <b-card :border-variant="getStepBorderVariant(step)" body-class="p-3">
                            <div class="d-flex align-items-start">
                                <div class="mr-3">
                                    <b-badge variant="primary" pill class="p-2">
                                        Step {{ step.stepNumber }}
                                    </b-badge>
                                </div>
                                <div class="flex-grow-1">
                                    <h6>{{ step.description }}</h6>
                                    <div v-if="step.compoundIngredientID != null" class="small text-muted">
                                        <i class="fas fa-link" />
                                        Weigh-out of <strong>{{ step.linkedIngredientName || 'a formula ingredient' }}</strong><!--
                                        A linked step need not declare a share: it may be the remainder, or it may carry
                                        the whole weigh-out with no proportion recorded, in which case there is no
                                        percentage to state and the clause is left off entirely.
                                        --><template v-if="step.isRemainderStep"> — the remainder after the other linked steps</template><template
                                            v-else-if="step.proportionPercent != null"> — {{ step.proportionPercent }}% of the weigh-out</template>
                                    </div>

                                    <!-- Weight Information -->
                                    <div v-if="step.expectedWeight || step.actualWeight" class="mt-2">
                                        <b-row>
                                            <b-col cols="4">
                                                <small class="text-muted">Expected Weight:</small><br />
                                                <strong v-if="step.expectedWeight">{{ step.expectedWeight.toFixed(2) }} {{ batch.compound?.unitOfMeasure }}</strong>
                                                <span v-else class="text-muted">N/A</span>
                                            </b-col>
                                            <b-col cols="4">
                                                <small class="text-muted">Actual Weight:</small><br />
                                                <strong v-if="step.actualWeight">{{ step.actualWeight.toFixed(2) }} {{ batch.compound?.unitOfMeasure }}</strong>
                                                <span v-else class="text-muted">Not recorded</span>
                                            </b-col>
                                            <b-col cols="4">
                                                <small class="text-muted">Tolerance Status:</small><br />
                                                <b-badge v-if="step.actualWeight != null && step.withinTolerance !== null"
                                                    :variant="step.withinTolerance ? 'success' : 'danger'">
                                                    {{ step.withinTolerance ? 'Within Tolerance' : 'Out of Tolerance' }}
                                                </b-badge>
                                                <span v-else class="text-muted">N/A</span>
                                            </b-col>
                                        </b-row>
                                    </div>

                                    <!-- Photo -->
                                    <div v-if="step.photoId" class="mt-3">
                                        <small class="text-muted">Photo Documentation:</small><br />
                                        <img v-if="shouldLoadImage(step.photoId)"
                                            :src="getImageUrl(step.photoId)"
                                            alt="Step Photo"
                                            class="img-thumbnail mt-2"
                                            style="max-width: 400px; cursor: pointer;"
                                            @click="showImageModal(step.photoId)"
                                            @error="onImageError(step.photoId)" />
                                        <div v-else class="mt-2">
                                            <b-button size="sm" variant="outline-primary" @click="loadImage(step.photoId)">
                                                <i class="fas fa-image" /> Load Photo
                                            </b-button>
                                        </div>
                                    </div>

                                    <!-- Completion Info -->
                                    <div v-if="step.completed" class="mt-2 pt-2 border-top">
                                        <small class="text-muted">
                                            <i class="fas fa-check-circle text-success" />
                                            Completed on {{ formatDateTime(step.completed) }} by {{ step.completedBy }}
                                        </small>
                                    </div>
                                </div>

                                <!-- Pharmacist Verification Toggle -->
                                <div v-if="step.requirePharmacistSignOff" class="ml-3 text-center" style="min-width: 120px;">
                                    <small class="text-muted d-block mb-1">Pharmacist Sign-Off</small>
                                    <b-form-checkbox
                                        :checked="step.pharmacistVerified === true"
                                        switch
                                        size="lg"
                                        @change="toggleStepVerification(step, $event)"
                                        :disabled="verifyingStepId === step.id" />
                                    <div v-if="step.pharmacistVerified && step.pharmacistVerifiedBy" class="mt-1">
                                        <small class="text-success">
                                            <i class="fas fa-check" /> {{ step.pharmacistVerifiedBy }}
                                        </small>
                                    </div>
                                </div>
                            </div>
                        </b-card>
                    </div>
                </div>
            </b-card>

            <!-- Action Buttons -->
            <b-card>
                <div class="d-flex justify-content-between">
                    <div class="d-flex align-items-center" style="gap: 8px;">
                        <b-button variant="outline-secondary" @click="goBack">
                            <i class="fas fa-arrow-left" /> Back to Queue
                        </b-button>
                        <!-- The batch record as a formula worksheet: potency line per ingredient, steps as
                             performed and the sign-offs, regenerated from the batch snapshot on every print. -->
                        <WorksheetPrinterSelect v-model="worksheetPrinter" :printers="worksheetPrinters" size="md" />
                        <b-button variant="outline-primary" :disabled="printingWorksheet" @click="printWorksheet"
                            v-b-tooltip.hover.ds750
                            title="Prints the batch record as a formula worksheet — the COA, activity factor, scaled and adjusted quantities and weigh-out per ingredient, the steps as performed and the sign-offs.">
                            <b-spinner v-if="printingWorksheet" small class="mr-1" />
                            <i v-else class="fas fa-print" /> Print Worksheet
                        </b-button>
                    </div>
                    <div class="d-flex" style="gap: 8px;">
                        <b-button
                            variant="danger"
                            @click="showRejectModal = true"
                            :disabled="submitting">
                            <i class="fas fa-times-circle" /> Reject
                        </b-button>
                        <PositiveIdGate
                            :enabled="positiveIdEnabled && positiveIdConfigLoaded && positiveIdTargetId !== null"
                            :enabled-methods="positiveIdMethods"
                            :action-type="PositiveIdActionType.Compound"
                            :target-entity-type="PositiveIdTargetEntityType.CompoundBatch"
                            :target-entity-id="positiveIdTargetId || ''"
                            @verified="onPositiveIdVerifiedForApprove">
                            <template #default="{ requestVerification, verifying }">
                                <b-button
                                    variant="success"
                                    @click="requestVerification"
                                    :disabled="!canApprove || submitting || verifying || positiveIdNotReady">
                                    <b-spinner v-if="submitting || verifying" small class="mr-1" />
                                    <i v-else class="fas fa-check-circle" /> Approve &amp; Post Inventory
                                </b-button>
                            </template>
                        </PositiveIdGate>
                    </div>
                </div>
                <div v-if="!canApprove" class="mt-2 text-right">
                    <small v-for="(reason, index) in approveBlockers" :key="index" class="text-warning d-block">
                        <i class="fas fa-exclamation-triangle" />
                        {{ reason }}
                    </small>
                </div>
            </b-card>
        </div>

        <b-alert v-else variant="warning" show>
            Batch not found or not in Awaiting Verification status.
        </b-alert>

        <!-- Image Modal -->
        <b-modal v-model="showImageModalFlag" title="Step Photo" size="xl" hide-footer>
            <img v-if="selectedImageId" :src="getImageUrl(selectedImageId)" alt="Step Photo" class="img-fluid" />
        </b-modal>

        <!-- Hidden reject gate — fires after the user confirms the reject modal so the verify step
             comes AFTER the user picks send-back-vs-permanent. Programmatic via ref since b-modal
             owns its own OK button. -->
        <PositiveIdGate
            ref="rejectGate"
            :enabled="positiveIdEnabled && positiveIdConfigLoaded && positiveIdTargetId !== null"
            :enabled-methods="positiveIdMethods"
            :action-type="PositiveIdActionType.Compound"
            :target-entity-type="PositiveIdTargetEntityType.CompoundBatch"
            :target-entity-id="positiveIdTargetId || ''"
            @verified="onPositiveIdVerifiedForReject" />

        <!-- Reject Modal -->
        <b-modal v-model="showRejectModal" title="Reject Batch" @ok="rejectBatch" ok-variant="danger" ok-title="Reject">
            <p>How would you like to handle this batch?</p>
            <b-form-group>
                <b-form-radio v-model="rejectSendBack" :value="true">
                    <strong>Send back for rework</strong>
                    <br /><small class="text-muted">Returns the batch to In Progress so the technician can make corrections.</small>
                </b-form-radio>
                <b-form-radio v-model="rejectSendBack" :value="false" class="mt-2">
                    <strong>Reject permanently</strong>
                    <br /><small class="text-muted">Marks the batch as Rejected. Ingredients already deducted will not be restored.</small>
                </b-form-radio>
            </b-form-group>
        </b-modal>
    </card>
</template>

<script lang="ts">
import { Component, Ref, Vue } from 'vue-property-decorator';
import { CompoundBatch, CompoundBatchSteps } from '@/models/Drug/CompoundDrug';
import Axios from 'axios';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';
import PositiveIdGate from '@/components/Common/PositiveIdGate.vue';
import CompoundBatchIngredientRecord from '@/components/Compound/CompoundBatchIngredientRecord.vue';
import WorksheetPrinterSelect from '@/components/Compound/WorksheetPrinterSelect.vue';
import { PrintDevice } from '@/services/PrintService';
import { loadWorksheetPrinters, printCompoundBatchWorksheet } from '@/util/compoundWorksheetPrint';
import { ACTIVITY_FACTOR_SETTINGS_OFF, ActivityFactorSettings, loadActivityFactorSettings } from '@/util/activityFactorApi';
import { describeReviewFlags, requiresReviewAcknowledgement, ReviewFlagDescription } from '@/util/compoundBatchReview';
import PositiveIdService, {
    PositiveIdActionType,
    PositiveIdTargetEntityType,
    POSITIVE_ID_TOKEN_HEADER,
} from '@/services/PositiveIdService';

@Component({
    name: "CompoundBatchVerificationDetailsPage",
    components: { PositiveIdGate, CompoundBatchIngredientRecord, WorksheetPrinterSelect },
})
export default class CompoundBatchVerificationDetailsPage extends Vue {
    PositiveIdActionType = PositiveIdActionType;
    PositiveIdTargetEntityType = PositiveIdTargetEntityType;

    @Ref('rejectGate') rejectGate!: any;

    private batch: CompoundBatch | null = null;
    private loading = false;
    private submitting = false;
    private acknowledging = false;
    private activityFactorSettings: ActivityFactorSettings = ACTIVITY_FACTOR_SETTINGS_OFF;
    private worksheetPrinters: PrintDevice[] = [];
    private worksheetPrinter: PrintDevice | null = null;
    private printingWorksheet = false;
    private verifyingStepId: number | null = null;
    private showImageModalFlag = false;
    private selectedImageId: number | null = null;
    private positiveIdEnabled = false;
    private positiveIdMethods: string[] | null = null;
    private positiveIdConfigLoaded = false;
    private verifiedToken = '';

    get positiveIdTargetId(): string | null {
        const n = +(this.batch?.id as any);
        return Number.isFinite(n) && n > 0 ? String(n) : null;
    }

    get positiveIdNotReady(): boolean {
        return this.positiveIdEnabled
            && (!this.positiveIdConfigLoaded || this.positiveIdTargetId === null);
    }
    private loadedImages: number[] = [];
    private imageUrls: { [key: number]: string } = {};
    private showRejectModal = false;
    private rejectSendBack = true;

    get pageTitle(): string {
        if (this.batch) {
            return `Verify Batch: ${this.batch.batchNumber}`;
        }
        return 'Compound Batch Verification';
    }

    get requiredStepCount(): number {
        return this.batch?.steps?.filter(s => s.requirePharmacistSignOff).length || 0;
    }

    get verifiedStepCount(): number {
        return this.batch?.steps?.filter(s => s.requirePharmacistSignOff && s.pharmacistVerified === true).length || 0;
    }

    /** The batch's review flags as the pharmacist reads them; empty when the record carries none. */
    get reviewFlagDescriptions(): ReviewFlagDescription[] {
        if (!this.batch) return [];
        return describeReviewFlags(this.batch, this.activityFactorSettings.decimalPlaces);
    }

    get reviewAcknowledged(): boolean {
        return !!this.batch?.reviewAcknowledgedBy;
    }

    /** True while flags exist that nobody has acknowledged — the server refuses to verify in this state. */
    get requiresAcknowledgement(): boolean {
        return requiresReviewAcknowledgement(this.batch);
    }

    /** Why Approve is disabled, one line each; empty when it is enabled. */
    get approveBlockers(): string[] {
        const reasons: string[] = [];
        if (this.requiredStepCount > 0 && this.verifiedStepCount < this.requiredStepCount) {
            reasons.push('All required step verifications must be completed before approving.');
        }
        if (this.requiresAcknowledgement) {
            reasons.push('The review flags must be acknowledged before approving.');
        }
        return reasons;
    }

    get canApprove(): boolean {
        if (!this.batch) return false;
        return this.approveBlockers.length === 0;
    }

    async mounted() {
        const batchId = this.$route.params.batchId;
        if (batchId) {
            await this.loadBatch(parseInt(batchId));
        }
        await Promise.all([this.fetchPositiveIdConfig(), this.loadActivityFactorSettings(), this.loadWorksheetPrinters()]);
    }

    async loadWorksheetPrinters() {
        const state = await loadWorksheetPrinters();
        this.worksheetPrinters = state.printers;
        this.worksheetPrinter = state.selected;
    }

    /**
     * Prints the batch record as a formula worksheet — to the chosen local printer, or to the
     * batch's store print queue.
     */
    async printWorksheet() {
        if (!this.batch) return;

        this.printingWorksheet = true;
        try {
            await printCompoundBatchWorksheet({
                batchId: this.batch.id,
                storeId: this.batch.storeId,
                printer: this.worksheetPrinter,
                apiBaseUrl: this.$http.defaults.baseURL?.replace('/api', ''),
                authProviderType: this.$authProviderType,
            });
            this.$notification(NotificationOptions.success('Formula worksheet sent to print'));
        } catch (error) {
            console.error('There was an error while printing the batch worksheet', { error, response: (error as any)?.response });
            this.$notification(NotificationOptions.error(error));
        } finally {
            this.printingWorksheet = false;
        }
    }

    /** Only the display precision is read here; the record shows what was saved whether or not the feature is on today. */
    async loadActivityFactorSettings() {
        this.activityFactorSettings = await loadActivityFactorSettings();
    }

    async fetchPositiveIdConfig() {
        const cfg = await PositiveIdService.getConfig();
        this.positiveIdEnabled = cfg.enabled;
        this.positiveIdMethods = cfg.methods;
        this.positiveIdConfigLoaded = true;
    }

    async onPositiveIdVerifiedForApprove(payload: { verifiedToken: string }) {
        this.verifiedToken = payload.verifiedToken;
        await this.approveBatch();
    }

    async onPositiveIdVerifiedForReject(payload: { verifiedToken: string }) {
        this.verifiedToken = payload.verifiedToken;
        await this.rejectBatch();
    }

    async onPositiveIdVerifiedForAcknowledge(payload: { verifiedToken: string }) {
        this.verifiedToken = payload.verifiedToken;
        await this.acknowledgeReview();
    }

    /**
     * Records the pharmacist's acknowledgement of the review flags. The response is the batch as it
     * now stands (acknowledged by / on), which replaces the loaded record so Approve enables without
     * a reload.
     */
    async acknowledgeReview() {
        if (!this.batch || !this.requiresAcknowledgement) return;
        this.acknowledging = true;

        try {
            const headers = this.verifiedToken ? { [POSITIVE_ID_TOKEN_HEADER]: this.verifiedToken } : undefined;
            const response = await Axios.post<CompoundBatch>(`/CompoundBatch/${this.batch.id}/acknowledge-review`, null,
                headers ? { headers } : undefined);
            this.verifiedToken = '';
            if (response.data) {
                this.batch = response.data;
            }
            this.$notification(NotificationOptions.success(`Review flags on batch ${this.batch.batchNumber} acknowledged.`));
        } catch (error) {
            this.verifiedToken = '';
            console.error('Error acknowledging review flags:', error);
            this.$notification(NotificationOptions.error(error));

            // A refusal means the server disagrees with the record on screen. The usual cause is a
            // second pharmacist acting from a page loaded before the first acknowledged: the card
            // would otherwise keep offering an acknowledgement that has already happened and keep
            // naming nobody, so reload to show who actually acknowledged. Only when the server
            // answered -- a network failure says nothing about the batch, and the reload would
            // just fail again behind a second toast.
            if (this.batch && (error as any)?.response) {
                await this.loadBatch(this.batch.id);
            }
        } finally {
            this.acknowledging = false;
        }
    }

    async loadBatch(batchId: number) {
        this.loading = true;
        try {
            const response = await Axios.get<CompoundBatch>(`/CompoundBatch/${batchId}`);
            this.batch = response.data;
        } catch (error) {
            console.error('Error loading batch:', error);
            this.$notification(NotificationOptions.error(error));
        } finally {
            this.loading = false;
        }
    }

    async toggleStepVerification(step: CompoundBatchSteps, verified: boolean) {
        if (!this.batch) return;
        this.verifyingStepId = step.id;

        try {
            await Axios.post(`/CompoundBatch/${this.batch.id}/step/${step.id}/verify`, { verified });
            step.pharmacistVerified = verified;
            if (verified) {
                step.pharmacistVerifiedBy = 'You';
                step.pharmacistVerifiedDate = new Date();
            } else {
                step.pharmacistVerifiedBy = null;
                step.pharmacistVerifiedDate = null;
            }
        } catch (error) {
            console.error('Error verifying step:', error);
            this.$notification(NotificationOptions.error(error));
        } finally {
            this.verifyingStepId = null;
        }
    }

    async approveBatch() {
        if (!this.batch || !this.canApprove) return;
        this.submitting = true;

        try {
            const headers = this.verifiedToken ? { [POSITIVE_ID_TOKEN_HEADER]: this.verifiedToken } : undefined;
            await Axios.post(`/CompoundBatch/${this.batch.id}/verify`, null, headers ? { headers } : undefined);
            this.verifiedToken = '';
            this.$notification(NotificationOptions.success(`Batch ${this.batch.batchNumber} has been verified and inventory has been posted.`));
            this.$router.push({ name: 'CompoundBatchVerificationQueue' });
        } catch (error) {
            this.verifiedToken = '';
            console.error('Error verifying batch:', error);
            this.$notification(NotificationOptions.error(error));
        } finally {
            this.submitting = false;
        }
    }

    async rejectBatch() {
        if (!this.batch) return;

        // Gate via the hidden reject gate. When verified, onPositiveIdVerifiedForReject re-enters
        // with a token in hand. When the feature flag is off the gate is a pass-through and this
        // branch never executes.
        if (this.positiveIdEnabled && !this.positiveIdConfigLoaded) {
            this.$notification(NotificationOptions.warning('Loading positive-ID configuration. Please retry in a moment.'));
            return;
        }
        if (this.positiveIdEnabled && this.positiveIdTargetId === null) {
            this.$notification(NotificationOptions.warning('Batch is still loading. Please retry once the page finishes loading.'));
            return;
        }
        if (this.positiveIdEnabled && !this.verifiedToken) {
            this.rejectGate.requestVerification();
            return;
        }

        this.submitting = true;

        try {
            const headers = this.verifiedToken ? { [POSITIVE_ID_TOKEN_HEADER]: this.verifiedToken } : undefined;
            await Axios.post(`/CompoundBatch/${this.batch.id}/reject`,
                { sendBackToInProgress: this.rejectSendBack },
                headers ? { headers } : undefined);
            this.verifiedToken = '';

            const message = this.rejectSendBack
                ? `Batch ${this.batch.batchNumber} has been sent back for rework.`
                : `Batch ${this.batch.batchNumber} has been rejected.`;

            this.$notification(NotificationOptions.success(message));
            this.$router.push({ name: 'CompoundBatchVerificationQueue' });
        } catch (error) {
            this.verifiedToken = '';
            console.error('Error rejecting batch:', error);
            this.$notification(NotificationOptions.error(error));
        } finally {
            this.submitting = false;
        }
    }

    goBack() {
        this.$router.push({ name: 'CompoundBatchVerificationQueue' });
    }

    getStepBorderVariant(step: CompoundBatchSteps): string {
        if (!step.requirePharmacistSignOff) return 'secondary';
        if (step.pharmacistVerified === true) return 'success';
        return 'warning';
    }

    // Image handling methods
    shouldLoadImage(photoId: number): boolean {
        return this.loadedImages.includes(photoId);
    }

    async loadImage(photoId: number) {
        if (this.loadedImages.includes(photoId)) return;

        try {
            const response = await Axios.get(`/Image/${photoId}`, { responseType: 'blob' });
            const imageUrl = URL.createObjectURL(response.data);
            this.$set(this.imageUrls, photoId, imageUrl);
            this.loadedImages.push(photoId);
        } catch (error) {
            console.error(`Failed to load image ${photoId}:`, error);
            this.$notification(NotificationOptions.error(`Failed to load image ${photoId}`));
        }
    }

    getImageUrl(photoId: number): string {
        return this.imageUrls[photoId] || '';
    }

    showImageModal(photoId: number) {
        this.selectedImageId = photoId;
        this.showImageModalFlag = true;
        this.loadImage(photoId);
    }

    onImageError(photoId: number) {
        console.error(`Failed to load image with ID: ${photoId}`);
    }

    formatDate(dateString: string | Date | null): string {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString();
    }

    formatDateTime(dateString: string | Date | null): string {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleString();
    }

    beforeDestroy() {
        Object.values(this.imageUrls).forEach(url => URL.revokeObjectURL(url));
    }
}
</script>

<style scoped>
.border-top {
    border-top: 1px solid #dee2e6;
}
</style>
